import {
  type AssetManifest,
  type ChestPlacement,
  footprintOf,
  TILE_SIZE,
  type ZoneDefinition,
} from '@shefira/shared';
import type Phaser from 'phaser';

/**
 * The zone's chests (ADR 0021), drawn as the server says each one looks to
 * this player: shut and ready, chained while its camp stands or it is not
 * theirs to open, open once they took what it held. The state comes in the
 * private `chests` message; until it does, a camp chest shows chained and a
 * hidden one shut.
 */

export type ChestLook = 'locked' | 'ready' | 'opened';

/** The sprite of each look, by manifest key. */
const SPRITES: Record<ChestLook, string> = {
  locked: 'prop.chest-locked',
  ready: 'prop.chest',
  opened: 'prop.chest-open',
};

export class ChestLayer {
  private readonly images = new Map<string, Phaser.GameObjects.Image>();
  private readonly looks = new Map<string, ChestLook>();
  /** Where each chest stands: the zone's, until the world editor moves one (ADR 0022). */
  private readonly placements = new Map<string, ChestPlacement>();

  constructor(
    private readonly scene: Phaser.Scene,
    zone: ZoneDefinition,
    private readonly manifest: AssetManifest,
  ) {
    for (const chest of zone.chests) {
      this.placements.set(chest.id, chest);
      this.looks.set(chest.id, chest.kind === 'camp' ? 'locked' : 'ready');
      this.draw(chest);
    }
  }

  /** What the server says: redraw the chests whose look changed. */
  apply(states: readonly { id: string; state: ChestLook }[]): void {
    for (const { id, state } of states) {
      if (this.looks.get(id) === state) continue;
      this.looks.set(id, state);
      const chest = this.placements.get(id);
      if (chest !== undefined) this.draw(chest);
    }
  }

  /** Draw a chest somewhere else: the world editor's preview, not the server's word. */
  move(chestId: string, tile: { x: number; y: number }): void {
    const chest = this.placements.get(chestId);
    if (chest === undefined || (chest.x === tile.x && chest.y === tile.y)) return;
    const moved = { ...chest, ...tile };
    this.placements.set(chestId, moved);
    this.draw(moved);
  }

  lookOf(chestId: string): ChestLook | undefined {
    return this.looks.get(chestId);
  }

  /** The chest drawn over a tile: its two tiles, and the row its lid rises into. */
  chestAt(tile: { x: number; y: number }): ChestPlacement | undefined {
    return [...this.placements.values()].find((chest) => {
      const box = footprintOf({ prop: 'chest', x: chest.x, y: chest.y });
      return tile.x >= box.x0 && tile.x <= box.x1 && tile.y >= chest.y - 1 && tile.y <= chest.y;
    });
  }

  /** The tile to walk to before opening it: in front, south of its left half. */
  static frontOf(chest: ChestPlacement): { x: number; y: number } {
    return { x: chest.x, y: chest.y + 1 };
  }

  private draw(chest: ChestPlacement): void {
    const look = this.looks.get(chest.id) ?? 'ready';
    const sprite = this.manifest.sprites[SPRITES[look]] ?? this.manifest.sprites[SPRITES.ready];
    if (sprite === undefined) return;
    this.images.get(chest.id)?.destroy();
    const frame = this.scene.textures.getFrame(sprite.atlas, sprite.frame);
    const box = footprintOf({ prop: 'chest', x: chest.x, y: chest.y });
    const middle = ((box.x0 + box.x1 + 1) * TILE_SIZE) / 2;
    const base = (chest.y + 1) * TILE_SIZE;
    // Whole pixels (CLAUDE.md rule 6), sorted with the characters by its base.
    const image = this.scene.add
      .image(Math.round(middle - frame.width / 2), base - frame.height, sprite.atlas, sprite.frame)
      .setOrigin(0, 0)
      .setDepth(base);
    this.images.set(chest.id, image);
  }
}
